import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';
import {
  ChevronLeft,
  CreditCard,
  DollarSign,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Archive,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Trash2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Tag,
  Percent,
  Ban,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { api } from '../../lib/api';
import { ConfirmModal } from '../../components/admin/ConfirmModal';

// ── Types ──────────────────────────────────────────────────────────────────

interface TierItem {
  upTo: number | null;
  unitAmount: number;
  flatAmount: number | null;
}

interface PriceItem {
  id: string;
  active: boolean;
  nickname: string | null;
  unitAmount: number | null;
  currency: string;
  interval: string;
  billingScheme: string;
  tiersMode: string | null;
  tiers: TierItem[] | null;
}

interface ProductItem {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  visible: boolean;
  prices: PriceItem[];
}

interface NewTier {
  upTo: string; // string for input handling; '' means infinity (last tier)
  unitAmount: string; // dollars as string for input
}

interface NewPriceForm {
  nickname: string;
  interval: 'month' | 'year';
  billingScheme: 'per_unit' | 'tiered';
  unitAmount: string; // dollars as string
  tiersMode: 'graduated' | 'volume';
  tiers: NewTier[];
}

// ── Coupon Types ──────────────────────────────────────────────────────────

interface PromotionCodeItem {
  id: string;
  code: string;
  active: boolean;
  maxRedemptions: number | null;
  timesRedeemed: number;
  expiresAt: number | null;
  restrictions: {
    firstTimeTransaction: boolean;
    customerIds: string[];
  };
}

interface CouponItem {
  id: string;
  name: string | null;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string;
  durationInMonths: number | null;
  maxRedemptions: number | null;
  timesRedeemed: number;
  redeemBy: number | null;
  valid: boolean;
  metadata: Record<string, string>;
  promotionCodes: PromotionCodeItem[];
}

interface NewCouponForm {
  name: string;
  type: 'percent' | 'fixed';
  percentOff: string;
  amountOff: string;
  code: string;
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths: string;
  appliesToProducts: boolean;
  productIds: string[];
  firstTimeOnly: boolean;
  limitToCustomer: boolean;
  customerStripeId: string;
  limitRedemptions: boolean;
  maxRedemptions: string;
  setExpiration: boolean;
  expiresAt: string; // date string YYYY-MM-DD
}

const EMPTY_COUPON_FORM: NewCouponForm = {
  name: '',
  type: 'percent',
  percentOff: '',
  amountOff: '',
  code: '',
  duration: 'forever',
  durationInMonths: '3',
  appliesToProducts: false,
  productIds: [],
  firstTimeOnly: false,
  limitToCustomer: false,
  customerStripeId: '',
  limitRedemptions: true,
  maxRedemptions: '1',
  setExpiration: false,
  expiresAt: '',
};

const EMPTY_PRICE_FORM: NewPriceForm = {
  nickname: '',
  interval: 'month',
  billingScheme: 'per_unit',
  unitAmount: '',
  tiersMode: 'graduated',
  tiers: [
    { upTo: '5', unitAmount: '' },
    { upTo: '', unitAmount: '' },
  ],
};

// ── Helpers ────────────────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function intervalLabel(interval: string): string {
  return interval === 'year' ? 'Annual' : 'Monthly';
}

// ── Plan Feature Types ────────────────────────────────────────────────────

type PlanFeatureKey =
  | 'dispatch' | 'jobs' | 'clientPortal' | 'quotePdfs'
  | 'smartMovingIntegration' | 'commercialMovingQuotes' | 'residentialMovingQuotes'
  | 'leadPipeline' | 'fleetManagement' | 'onlineLeadCapture'
  | 'stripeConnect' | 'customerPayments';

const PLAN_FEATURE_OPTIONS: Array<{ key: PlanFeatureKey; label: string; desc: string }> = [
  { key: 'commercialMovingQuotes', label: 'Commercial Moving Quotes', desc: 'Commercial moving quote functionality' },
  { key: 'residentialMovingQuotes', label: 'Residential Moving Quotes', desc: 'Residential moving quote functionality' },
  { key: 'dispatch', label: 'Dispatch', desc: 'Dispatch board for assigning estimators and movers' },
  { key: 'jobs', label: 'Jobs', desc: 'Job tracking, crew/truck assignment, and sign-off' },
  { key: 'fleetManagement', label: 'Fleet Management', desc: 'Truck and warehouse management' },
  { key: 'leadPipeline', label: 'Lead Pipeline', desc: 'Lead tracking, follow-ups, and lead-to-quote conversion' },
  { key: 'onlineLeadCapture', label: 'Online Lead Capture', desc: 'Public booking form for capturing leads' },
  { key: 'clientPortal', label: 'Client Portal', desc: 'Shareable online quote links for customers' },
  { key: 'quotePdfs', label: 'Quote PDFs', desc: 'Generate PDF quotes via CraftMyPDF' },
  { key: 'customerPayments', label: 'Customer Payments', desc: 'Accept and track payments from customers' },
  { key: 'stripeConnect', label: 'Stripe Connect', desc: 'Credit/debit card payment processing' },
  { key: 'smartMovingIntegration', label: 'SmartMoving Integration', desc: 'Sync with SmartMoving CRM' },
];

// ── Component ──────────────────────────────────────────────────────────────

export function ManagePricingPage() {
  const navigate = useNavigate();
  const { currentUser } = useUserProfile();

  // ── Stripe mode (test = sandbox / demo, live = production) ─────────────
  const [stripeAdminMode, setStripeAdminMode] = useState<'test' | 'live'>('test');
  const modeQuery = `?mode=${stripeAdminMode}`;
  const isLive = stripeAdminMode === 'live';

  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track which product's "Add Price" form is open
  const [addPriceOpenFor, setAddPriceOpenFor] = useState<string | null>(null);
  const [priceForm, setPriceForm] = useState<NewPriceForm>({ ...EMPTY_PRICE_FORM });
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Inline action loading states
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  // ── Create Product state ────────────────────────────────────────────────
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductDesc, setNewProductDesc] = useState('');
  const [newProductFlatRate, setNewProductFlatRate] = useState('');
  const [newProductAnnualRate, setNewProductAnnualRate] = useState('');
  const [newProductSeatLimit, setNewProductSeatLimit] = useState('');
  const [newProductNoSeatMax, setNewProductNoSeatMax] = useState(true);
  const [newProductFeatures, setNewProductFeatures] = useState<Partial<Record<PlanFeatureKey, boolean>>>({});
  const [newProductTrialDays, setNewProductTrialDays] = useState('');
  const [createProductSaving, setCreateProductSaving] = useState(false);
  const [createProductError, setCreateProductError] = useState<string | null>(null);

  // ── Coupon state ────────────────────────────────────────────────────────
  const [coupons, setCoupons] = useState<CouponItem[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(true);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponForm, setCouponForm] = useState<NewCouponForm>({ ...EMPTY_COUPON_FORM });
  const [couponSaving, setCouponSaving] = useState(false);
  const [couponFormError, setCouponFormError] = useState<string | null>(null);
  const [deactivatingCoupon, setDeactivatingCoupon] = useState<CouponItem | null>(null);

  // ── Platform fee state ──────────────────────────────────────────────────
  const [feeType, setFeeType] = useState<'percentage' | 'flat' | 'percentage_plus_flat'>('percentage');
  const [feePercentage, setFeePercentage] = useState('2.5');
  const [feeFlatCents, setFeeFlatCents] = useState('0');
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeSuccess, setFeeSuccess] = useState(false);
  const [feeLoaded, setFeeLoaded] = useState(false);

  // ── Test card state (only for test mode) ──────────────────────────────────
  const [testCardNumber, setTestCardNumber] = useState('');
  const [testCardSaving, setTestCardSaving] = useState(false);
  const [testCardSuccess, setTestCardSuccess] = useState(false);
  const [testCardLoaded, setTestCardLoaded] = useState(false);

  // Load platform fee + test card number from appConfig
  useEffect(() => {
    let cancelled = false;
    api.get<{ success: boolean; data: {
      stripeTestCardNumber?: string;
      platformFee?: { type: string; percentage?: number; flatAmountCents?: number };
    } }>('/admin/app-config')
      .then((res) => {
        if (cancelled) return;
        if (res.data.stripeTestCardNumber) setTestCardNumber(res.data.stripeTestCardNumber);
        if (res.data.platformFee) {
          const pf = res.data.platformFee;
          if (pf.type === 'percentage' || pf.type === 'flat' || pf.type === 'percentage_plus_flat') {
            setFeeType(pf.type);
          }
          if (pf.percentage !== undefined) setFeePercentage(String(pf.percentage));
          if (pf.flatAmountCents !== undefined) setFeeFlatCents(String(pf.flatAmountCents));
        }
        setFeeLoaded(true);
        setTestCardLoaded(true);
      })
      .catch(() => { if (!cancelled) { setFeeLoaded(true); setTestCardLoaded(true); } });
    return () => { cancelled = true; };
  }, []);

  const formatCardDisplay = (digits: string): string => {
    const clean = digits.replace(/\D/g, '').slice(0, 16);
    return clean.replace(/(.{4})/g, '$1 ').trim();
  };

  const handleTestCardChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    setTestCardNumber(digits);
    setTestCardSuccess(false);
  };

  const handleSaveTestCard = async () => {
    setTestCardSaving(true);
    setTestCardSuccess(false);
    try {
      await api.patch('/admin/app-config', { stripeTestCardNumber: testCardNumber });
      setTestCardSuccess(true);
      setTimeout(() => setTestCardSuccess(false), 3000);
    } catch {
      setError('Failed to save test card number.');
    } finally {
      setTestCardSaving(false);
    }
  };

  // Load products (re-fetch when mode changes)
  useEffect(() => {
    let cancelled = false;
    setProducts([]);
    setLoading(true);
    setError(null);
    api
      .get<{ success: boolean; data: ProductItem[] }>(`/pricing/products${modeQuery}`)
      .then((res) => { if (!cancelled) setProducts(res.data); })
      .catch((err: unknown) => {
        if (!cancelled) {
          setProducts([]);
          setError(err instanceof Error ? err.message : 'Failed to load products.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [modeQuery]);

  // Load coupons (re-fetch when mode changes)
  const fetchCoupons = useCallback(() => {
    setCoupons([]);
    setCouponsLoading(true);
    setCouponError(null);
    api
      .get<{ success: boolean; data: CouponItem[] }>(`/pricing/coupons${modeQuery}`)
      .then((res) => setCoupons(res.data))
      .catch((err: unknown) => {
        setCoupons([]);
        setCouponError(err instanceof Error ? err.message : 'Failed to load coupons.');
      })
      .finally(() => setCouponsLoading(false));
  }, [modeQuery]);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  if (!currentUser?.isPlatformAdmin && IS_FIREBASE_CONFIGURED) {
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-2xl md:mx-auto">
        <p className="text-small text-slate text-center py-16">
          You don't have access to pricing management.
        </p>
      </div>
    );
  }

  // ── Platform Fee Handler ───────────────────────────────────────────────

  const handleSavePlatformFee = async () => {
    setFeeSaving(true);
    setFeeSuccess(false);
    try {
      const platformFee: Record<string, unknown> = { type: feeType };
      if (feeType === 'percentage' || feeType === 'percentage_plus_flat') {
        platformFee.percentage = parseFloat(feePercentage) || 0;
      }
      if (feeType === 'flat' || feeType === 'percentage_plus_flat') {
        platformFee.flatAmountCents = parseInt(feeFlatCents, 10) || 0;
      }
      await api.patch('/admin/app-config', { platformFee });
      setFeeSuccess(true);
      setTimeout(() => setFeeSuccess(false), 3000);
    } catch {
      setError('Failed to save platform fee.');
    } finally {
      setFeeSaving(false);
    }
  };

  // ── Create Product Handler ─────────────────────────────────────────────

  const handleCreateProduct = async () => {
    if (!newProductName.trim()) return;
    const monthlyRate = newProductFlatRate !== '' ? parseFloat(newProductFlatRate) : NaN;
    const annualRate = newProductAnnualRate !== '' ? parseFloat(newProductAnnualRate) : NaN;
    const hasMonthly = !isNaN(monthlyRate) && monthlyRate >= 0;
    const hasAnnual = !isNaN(annualRate) && annualRate >= 0;
    if (!hasMonthly && !hasAnnual) {
      setCreateProductError('Enter at least one rate (monthly or annual). $0 is valid for free tiers.');
      return;
    }
    if (newProductFlatRate !== '' && (!hasMonthly || monthlyRate < 0)) {
      setCreateProductError('Monthly rate must be $0 or more.');
      return;
    }
    if (newProductAnnualRate !== '' && (!hasAnnual || annualRate < 0)) {
      setCreateProductError('Annual rate must be $0 or more.');
      return;
    }
    const trialDays = parseInt(newProductTrialDays, 10);
    setCreateProductSaving(true);
    setCreateProductError(null);
    try {
      const res = await api.post<{ success: boolean; data: ProductItem }>(
        `/pricing/products${modeQuery}`,
        {
          name: newProductName.trim(),
          description: newProductDesc.trim() || undefined,
          ...(hasMonthly ? { flatMonthlyRate: monthlyRate } : {}),
          ...(hasAnnual ? { flatAnnualRate: annualRate } : {}),
          ...(trialDays > 0 ? { trialDays } : {}),
        },
      );
      const newProduct = res.data;
      setProducts((prev) => [...prev, newProduct]);

      // Save plan features + seat limit for the new product
      const hasFeatures = Object.values(newProductFeatures).some(Boolean);
      if (hasFeatures || !newProductNoSeatMax) {
        try {
          await api.put(`/admin/plan-features/${newProduct.id}`, {
            productName: newProductName.trim(),
            allowedFeatures: newProductFeatures,
            ...(!newProductNoSeatMax && newProductSeatLimit
              ? { maxUsers: parseInt(newProductSeatLimit, 10) || null }
              : { maxUsers: null }),
          });
        } catch {
          // Product was created but features failed — not critical
          setCreateProductError('Product created, but failed to save feature mapping. Edit it from the product card.');
        }
      }

      setNewProductName('');
      setNewProductDesc('');
      setNewProductFlatRate('');
      setNewProductAnnualRate('');
      setNewProductSeatLimit('');
      setNewProductNoSeatMax(true);
      setNewProductFeatures({});
      setNewProductTrialDays('');
      setShowCreateProduct(false);
    } catch (err: unknown) {
      setCreateProductError(err instanceof Error ? err.message : 'Failed to create product.');
    } finally {
      setCreateProductSaving(false);
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleToggleVisibility = async (productId: string, currentlyVisible: boolean) => {
    setActionLoading((prev) => ({ ...prev, [`vis-${productId}`]: true }));
    try {
      await api.patch<{ success: boolean }>(`/pricing/products/${productId}/visibility${modeQuery}`, {
        visible: !currentlyVisible,
      });
      setProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, visible: !currentlyVisible } : p)),
      );
      setActionSuccess(`vis-${productId}`);
      setTimeout(() => setActionSuccess(null), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to toggle visibility.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [`vis-${productId}`]: false }));
    }
  };

  const handleDeactivatePrice = async (priceId: string) => {
    setActionLoading((prev) => ({ ...prev, [priceId]: true }));
    try {
      await api.patch<{ success: boolean }>(`/pricing/prices/${priceId}/deactivate${modeQuery}`);
      setProducts((prev) =>
        prev.map((p) => ({
          ...p,
          prices: p.prices.map((pr) => (pr.id === priceId ? { ...pr, active: false } : pr)),
        })),
      );
      setActionSuccess(priceId);
      setTimeout(() => setActionSuccess(null), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to archive price.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [priceId]: false }));
    }
  };

  const handleActivatePrice = async (priceId: string) => {
    setActionLoading((prev) => ({ ...prev, [priceId]: true }));
    try {
      await api.patch<{ success: boolean }>(`/pricing/prices/${priceId}/activate${modeQuery}`);
      setProducts((prev) =>
        prev.map((p) => ({
          ...p,
          prices: p.prices.map((pr) => (pr.id === priceId ? { ...pr, active: true } : pr)),
        })),
      );
      setActionSuccess(priceId);
      setTimeout(() => setActionSuccess(null), 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to activate price.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [priceId]: false }));
    }
  };

  const handleOpenAddPrice = (productId: string) => {
    setAddPriceOpenFor(productId);
    setPriceForm({ ...EMPTY_PRICE_FORM });
    setFormError(null);
  };

  const handleCloseAddPrice = () => {
    setAddPriceOpenFor(null);
    setFormError(null);
  };

  const handleAddTier = () => {
    setPriceForm((prev) => {
      const tiers = [...prev.tiers];
      // Insert before the last tier (the "infinity" tier)
      tiers.splice(tiers.length - 1, 0, { upTo: '', unitAmount: '' });
      return { ...prev, tiers };
    });
  };

  const handleRemoveTier = (index: number) => {
    setPriceForm((prev) => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index),
    }));
  };

  const handleUpdateTier = (index: number, field: keyof NewTier, value: string) => {
    setPriceForm((prev) => ({
      ...prev,
      tiers: prev.tiers.map((t, i) => (i === index ? { ...t, [field]: value } : t)),
    }));
  };

  const handleSubmitPrice = async () => {
    if (!addPriceOpenFor) return;

    setFormSaving(true);
    setFormError(null);

    try {
      const body: Record<string, unknown> = {
        nickname: priceForm.nickname || undefined,
        interval: priceForm.interval,
        billingScheme: priceForm.billingScheme,
        currency: 'usd',
      };

      if (priceForm.billingScheme === 'per_unit') {
        const dollars = parseFloat(priceForm.unitAmount);
        if (isNaN(dollars) || dollars < 0) {
          setFormError('Enter a valid price amount.');
          setFormSaving(false);
          return;
        }
        body.unitAmount = Math.round(dollars * 100); // convert to cents
      } else {
        body.tiersMode = priceForm.tiersMode;

        const tiers = priceForm.tiers.map((t, i) => {
          const dollars = parseFloat(t.unitAmount);
          if (isNaN(dollars) || dollars < 0) {
            throw new Error(`Tier ${i + 1}: enter a valid price per user.`);
          }
          const isLast = i === priceForm.tiers.length - 1;
          const upTo = isLast ? null : parseInt(t.upTo, 10);
          if (!isLast && (isNaN(upTo as number) || (upTo as number) < 1)) {
            throw new Error(`Tier ${i + 1}: enter a valid "up to" user count.`);
          }
          return { upTo, unitAmount: Math.round(dollars * 100) };
        });

        body.tiers = tiers;
      }

      const res = await api.post<{ success: boolean; data: PriceItem }>(
        `/pricing/products/${addPriceOpenFor}/prices${modeQuery}`,
        body,
      );

      // Add the new price to the product in state
      setProducts((prev) =>
        prev.map((p) =>
          p.id === addPriceOpenFor
            ? { ...p, prices: [res.data, ...p.prices] }
            : p,
        ),
      );

      handleCloseAddPrice();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to create price.');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Coupon handlers ───────────────────────────────────────────────────

  const handleOpenCouponForm = () => {
    setShowCouponForm(true);
    setCouponForm({ ...EMPTY_COUPON_FORM });
    setCouponFormError(null);
  };

  const handleCloseCouponForm = () => {
    setShowCouponForm(false);
    setCouponFormError(null);
  };

  const handleSubmitCoupon = async () => {
    setCouponSaving(true);
    setCouponFormError(null);

    try {
      if (!couponForm.name.trim()) throw new Error('Coupon name is required.');
      if (!couponForm.code.trim()) throw new Error('Promotion code is required.');

      if (couponForm.duration === 'repeating') {
        const months = parseInt(couponForm.durationInMonths, 10);
        if (isNaN(months) || months < 1) throw new Error('Duration in months must be at least 1.');
      }

      const body: Record<string, unknown> = {
        name: couponForm.name.trim(),
        type: couponForm.type,
        code: couponForm.code.trim(),
        duration: couponForm.duration,
        durationInMonths: couponForm.duration === 'repeating' ? parseInt(couponForm.durationInMonths, 10) : undefined,
        appliesToProducts: couponForm.appliesToProducts,
        productIds: couponForm.appliesToProducts ? couponForm.productIds : [],
        firstTimeOnly: couponForm.firstTimeOnly,
        limitToCustomer: couponForm.limitToCustomer,
        customerStripeId: couponForm.limitToCustomer ? couponForm.customerStripeId : undefined,
        limitRedemptions: couponForm.limitRedemptions,
        maxRedemptions: couponForm.limitRedemptions ? parseInt(couponForm.maxRedemptions, 10) || 1 : undefined,
        setExpiration: couponForm.setExpiration,
      };

      if (couponForm.type === 'percent') {
        const pct = parseFloat(couponForm.percentOff);
        if (isNaN(pct) || pct < 0.01 || pct > 100) throw new Error('Percent off must be between 0.01 and 100.');
        body.percentOff = pct;
      } else {
        const amt = parseFloat(couponForm.amountOff);
        if (isNaN(amt) || amt < 0.01) throw new Error('Amount off must be at least $0.01.');
        body.amountOff = Math.round(amt * 100); // Convert to cents
      }

      if (couponForm.setExpiration && couponForm.expiresAt) {
        body.expiresAt = Math.floor(new Date(couponForm.expiresAt + 'T23:59:59').getTime() / 1000);
      }

      if (couponForm.limitToCustomer && !couponForm.customerStripeId) {
        throw new Error('Please select a customer.');
      }

      const res = await api.post<{ success: boolean; data: CouponItem }>(`/pricing/coupons${modeQuery}`, body);
      setCoupons((prev) => [res.data, ...prev]);
      handleCloseCouponForm();
    } catch (err: unknown) {
      setCouponFormError(err instanceof Error ? err.message : 'Failed to create coupon.');
    } finally {
      setCouponSaving(false);
    }
  };

  const handleDeactivateCoupon = async (coupon: CouponItem) => {
    setActionLoading((prev) => ({ ...prev, [`coupon-${coupon.id}`]: true }));
    try {
      await api.patch<{ success: boolean }>(`/pricing/coupons/${coupon.id}/deactivate${modeQuery}`);
      setCoupons((prev) => prev.filter((c) => c.id !== coupon.id));
      setActionSuccess(`coupon-${coupon.id}`);
      setTimeout(() => setActionSuccess(null), 2000);
    } catch (err: unknown) {
      setCouponError(err instanceof Error ? err.message : 'Failed to deactivate coupon.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [`coupon-${coupon.id}`]: false }));
      setDeactivatingCoupon(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-3xl md:mx-auto">
      {/* Header */}
      <button
        type="button"
        onClick={() => navigate('/admin/platform-tools')}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors duration-150 -ml-1"
      >
        <ChevronLeft size={16} />
        Platform Admin Tools
      </button>

      <h1 className="font-display text-h1 text-midnight mb-1">Manage Pricing and Coupons</h1>
      <p className="text-small text-slate mb-4">
        View and manage Stripe products, prices, and coupons. Toggle visibility to control what customers see.
      </p>

      {/* ── Stripe Mode Toggle ──────────────────────────────────────────── */}
      <div className={`rounded-xl border-2 p-4 mb-4 transition-colors duration-200 ${
        isLive
          ? 'border-red-400 bg-red-50'
          : 'border-amber-300 bg-amber-50'
      }`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {isLive ? (
              <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
            ) : (
              <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
            )}
            <div className="min-w-0">
              <p className={`text-[14px] font-bold ${isLive ? 'text-red-800' : 'text-amber-800'}`}>
                {isLive ? 'LIVE MODE — Real Stripe Account' : 'TEST MODE — Stripe Sandbox'}
              </p>
              <p className={`text-[12px] mt-0.5 leading-snug ${isLive ? 'text-red-600' : 'text-amber-700'}`}>
                {isLive
                  ? 'Changes here affect real customers and real charges. Proceed with caution.'
                  : 'You are viewing sandbox data used by demo organizations.'}
              </p>
            </div>
          </div>

          {/* Toggle switch */}
          <button
            type="button"
            role="switch"
            aria-checked={isLive}
            onClick={() => setStripeAdminMode(isLive ? 'test' : 'live')}
            className={`relative shrink-0 w-[72px] h-8 rounded-full transition-colors duration-200
              ${isLive
                ? 'bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.3)]'
                : 'bg-amber-400'
              }`}
          >
            <span
              className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-200
                ${isLive ? 'translate-x-10' : 'translate-x-0'}`}
            />
            <span className={`absolute inset-0 flex items-center text-[10px] font-bold uppercase tracking-wider text-white
              ${isLive ? 'justify-start pl-2.5' : 'justify-end pr-2'}`}>
              {isLive ? 'Live' : 'Test'}
            </span>
          </button>
        </div>
      </div>

      {/* Live mode confirmation banner */}
      {isLive && (
        <div className="rounded-xl border-2 border-red-500 bg-red-600 text-white p-4 mb-4
                        flex items-center gap-3 animate-pulse-once">
          <AlertTriangle size={22} className="shrink-0" />
          <p className="text-[13px] font-semibold leading-snug">
            You are managing <span className="underline">production</span> Stripe products and coupons.
            Any changes will immediately affect paying customers.
          </p>
        </div>
      )}

      {/* Test card number (test mode only) */}
      {!isLive && testCardLoaded && (
        <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mb-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={18} className="text-plum" />
            <h2 className="font-display text-h3 text-midnight">Test Card Number</h2>
          </div>
          <p className="text-small text-slate mb-3 leading-relaxed">
            Enter a Stripe test card number. This will be shown to demo organization admins
            so they can test the checkout flow in the Stripe Sandbox environment.
          </p>

          <div className="flex gap-2 mb-3">
            <input
              type="text"
              inputMode="numeric"
              value={formatCardDisplay(testCardNumber)}
              onChange={(e) => handleTestCardChange(e.target.value)}
              placeholder="4242 4242 4242 4242"
              className="flex-1 min-w-0 px-3 py-2.5 rounded-lg border border-stone
                         bg-white text-body text-midnight font-mono tracking-wider placeholder:text-slate/50
                         focus:outline-none focus:ring-2 focus:ring-plum/40 focus:border-plum transition-colors"
            />
            <button
              type="button"
              onClick={handleSaveTestCard}
              disabled={testCardSaving || testCardNumber.length < 13}
              className="btn btn-primary px-5 shrink-0"
            >
              {testCardSaving ? 'Saving…' : testCardSuccess ? 'Saved!' : 'Save'}
            </button>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
            <p className="text-[13px] text-amber-800 font-semibold mb-1">
              Stripe Sandbox test card
            </p>
            <p className="text-[12px] text-amber-700 leading-relaxed">
              This is a test card number provided by Stripe for testing in the Sandbox/Test environment.
              It will not result in a real charge.
            </p>
            <div className="mt-2 flex flex-col gap-1 text-[12px] text-amber-700">
              <span><span className="font-semibold">Expiration date:</span> Any date in the future</span>
              <span><span className="font-semibold">Security code (CVC):</span> Any three digits</span>
            </div>
          </div>
        </section>
      )}

      {/* Global error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
          <AlertCircle size={18} className="shrink-0 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-small text-red-700">{error}</p>
          </div>
          <button type="button" onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <Trash2 size={14} />
          </button>
        </div>
      )}

      {/* Create Product */}
      {!loading && (
        <div className="mb-4">
          {!showCreateProduct ? (
            <button
              type="button"
              onClick={() => setShowCreateProduct(true)}
              className="flex items-center gap-2 text-small font-medium text-plum
                         hover:text-plum-dark transition-colors"
            >
              <Plus size={16} />
              Create Product
            </button>
          ) : (
            <div className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm">
              <h3 className="font-display text-h3 text-midnight mb-1">New Product</h3>
              <p className="text-[12px] text-slate mb-4">
                Create a flat-rate plan with monthly and/or annual pricing, included features, and optional user limit.
              </p>

              <div className="space-y-3">
                {/* Name */}
                <div>
                  <label className="block text-[13px] font-medium text-midnight mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newProductName}
                    onChange={(e) => setNewProductName(e.target.value)}
                    placeholder="e.g. Starter, Pro, Enterprise"
                    className="w-full px-3 py-2 rounded-lg border border-stone text-small text-midnight
                               placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                               focus:border-plum"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-[13px] font-medium text-midnight mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newProductDesc}
                    onChange={(e) => setNewProductDesc(e.target.value)}
                    placeholder="Optional description"
                    className="w-full px-3 py-2 rounded-lg border border-stone text-small text-midnight
                               placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                               focus:border-plum"
                  />
                </div>

                {/* Rates */}
                <div>
                  <p className="text-[13px] font-medium text-midnight mb-1">
                    Pricing <span className="text-red-500">*</span>
                  </p>
                  <p className="text-[11px] text-slate mb-2">
                    Enter at least one rate. $0 is valid for free tiers. If both are provided, the product will have Monthly and Annual pricing options.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                        Monthly Rate
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate text-small">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newProductFlatRate}
                          onChange={(e) => setNewProductFlatRate(e.target.value)}
                          placeholder="49.00"
                          className="w-full px-3 py-2 pl-7 rounded-lg border border-stone text-small text-midnight
                                     placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                                     focus:border-plum"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                        Annual Rate
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate text-small">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newProductAnnualRate}
                          onChange={(e) => setNewProductAnnualRate(e.target.value)}
                          placeholder="499.00"
                          className="w-full px-3 py-2 pl-7 rounded-lg border border-stone text-small text-midnight
                                     placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                                     focus:border-plum"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trial Period */}
                <div>
                  <label className="block text-[13px] font-medium text-midnight mb-1">
                    Trial Period (days)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={newProductTrialDays}
                    onChange={(e) => setNewProductTrialDays(e.target.value)}
                    placeholder="e.g. 14"
                    className="w-32 px-3 py-2 rounded-lg border border-stone text-small text-midnight
                               placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                               focus:border-plum"
                  />
                  <p className="text-[11px] text-slate mt-1">
                    {newProductTrialDays && parseInt(newProductTrialDays, 10) > 0
                      ? `New subscribers get ${newProductTrialDays} day${parseInt(newProductTrialDays, 10) === 1 ? '' : 's'} free before billing starts.`
                      : 'Leave empty for no trial. Billing starts immediately at checkout.'}
                  </p>
                </div>

                {/* User Limit */}
                <div>
                  <label className="block text-[13px] font-medium text-midnight mb-1">
                    User Limit
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => { setNewProductNoSeatMax(!newProductNoSeatMax); if (!newProductNoSeatMax) setNewProductSeatLimit(''); }}
                      className="flex items-center gap-2 text-left py-1 shrink-0"
                    >
                      <div className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                                       ${newProductNoSeatMax
                                         ? 'bg-plum border-plum'
                                         : 'bg-white border-stone hover:border-plum/40'}`}>
                        {newProductNoSeatMax && <CheckCircle2 size={14} className="text-white" />}
                      </div>
                      <span className="text-small text-midnight">No user maximum</span>
                    </button>
                    {!newProductNoSeatMax && (
                      <input
                        type="number"
                        min="1"
                        value={newProductSeatLimit}
                        onChange={(e) => setNewProductSeatLimit(e.target.value)}
                        placeholder="e.g. 3"
                        className="w-20 px-3 py-2 rounded-lg border border-stone text-small text-midnight text-center
                                   placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                                   focus:border-plum"
                      />
                    )}
                  </div>
                  <p className="text-[11px] text-slate mt-1">
                    {newProductNoSeatMax
                      ? 'Orgs on this plan can have unlimited users.'
                      : 'Maximum number of users an org on this plan can have.'}
                  </p>
                </div>

                {/* Included Features */}
                <div>
                  <label className="block text-[13px] font-medium text-midnight mb-2">
                    Included Features
                  </label>
                  <div className="border border-stone rounded-lg p-3 space-y-1 max-h-64 overflow-y-auto">
                    {PLAN_FEATURE_OPTIONS.map(({ key, label, desc }) => (
                      <label
                        key={key}
                        className="flex items-start gap-3 py-1.5 px-2 rounded-lg hover:bg-mist/50
                                   transition-colors cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={!!newProductFeatures[key]}
                          onChange={() => setNewProductFeatures(prev => ({ ...prev, [key]: !prev[key] }))}
                          className="mt-0.5 h-4 w-4 rounded border-stone text-plum focus:ring-plum/30 shrink-0"
                        />
                        <div className="min-w-0">
                          <span className="text-[13px] font-medium text-midnight">{label}</span>
                          <p className="text-[11px] text-slate leading-snug">{desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate mt-1">
                    {Object.values(newProductFeatures).filter(Boolean).length} feature{Object.values(newProductFeatures).filter(Boolean).length !== 1 ? 's' : ''} selected
                  </p>
                </div>
              </div>

              {createProductError && (
                <div className="mt-3 flex items-center gap-2 text-[12px] text-red-600">
                  <AlertCircle size={14} />
                  {createProductError}
                </div>
              )}

              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCreateProduct}
                  disabled={createProductSaving || !newProductName.trim() || (newProductFlatRate === '' && newProductAnnualRate === '')}
                  className="px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-plum
                             hover:bg-plum-dark disabled:opacity-50 transition-colors
                             flex items-center gap-2"
                >
                  {createProductSaving && <Loader2 size={14} className="animate-spin" />}
                  Create Product
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreateProduct(false); setNewProductName(''); setNewProductDesc(''); setNewProductFlatRate(''); setNewProductAnnualRate(''); setNewProductSeatLimit(''); setNewProductNoSeatMax(true); setNewProductFeatures({}); setNewProductTrialDays(''); setCreateProductError(null); }}
                  className="px-4 py-2 rounded-lg text-[13px] font-medium text-slate
                             hover:text-midnight transition-colors"
                >
                  Cancel
                </button>
              </div>

              <p className="text-[11px] text-slate mt-2">
                The product will be created as hidden. Make it visible when ready.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-plum" />
        </div>
      )}

      {/* Empty state */}
      {!loading && products.length === 0 && !showCreateProduct && (
        <div className="bg-mist border border-stone rounded-xl p-6 text-center">
          <DollarSign size={32} className="mx-auto text-slate mb-3" />
          <p className="text-small text-slate">
            No products found in Stripe. Use the "Create Product" button above to get started.
          </p>
        </div>
      )}

      {/* Product list */}
      {!loading && products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          actionLoading={actionLoading}
          actionSuccess={actionSuccess}
          isAddPriceOpen={addPriceOpenFor === product.id}
          priceForm={priceForm}
          formSaving={formSaving}
          formError={formError}
          onToggleVisibility={() => handleToggleVisibility(product.id, product.visible)}
          onDeactivatePrice={handleDeactivatePrice}
          onActivatePrice={handleActivatePrice}
          onOpenAddPrice={() => handleOpenAddPrice(product.id)}
          onCloseAddPrice={handleCloseAddPrice}
          onUpdateForm={setPriceForm}
          onAddTier={handleAddTier}
          onRemoveTier={handleRemoveTier}
          onUpdateTier={handleUpdateTier}
          onSubmitPrice={handleSubmitPrice}
        />
      ))}

      {/* ── Coupons Section ──────────────────────────────────────────────── */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-h2 text-midnight">Coupons</h2>
            <p className="text-small text-slate mt-0.5">
              Create and manage discount coupons. All coupons are customer-facing.
            </p>
          </div>
          {!showCouponForm && (
            <button
              type="button"
              onClick={handleOpenCouponForm}
              className="shrink-0 btn btn-primary flex items-center gap-2"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">New Coupon</span>
              <span className="sm:hidden">New</span>
            </button>
          )}
        </div>

        {/* Coupon error */}
        {couponError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <AlertCircle size={18} className="shrink-0 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-small text-red-700">{couponError}</p>
            </div>
            <button type="button" onClick={() => setCouponError(null)} className="text-red-400 hover:text-red-600">
              <Trash2 size={14} />
            </button>
          </div>
        )}

        {/* Create coupon form */}
        {showCouponForm && (
          <CreateCouponForm
            form={couponForm}
            saving={couponSaving}
            error={couponFormError}
            products={products}
            onUpdate={setCouponForm}
            onSubmit={handleSubmitCoupon}
            onCancel={handleCloseCouponForm}
          />
        )}

        {/* Coupon list loading */}
        {couponsLoading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={24} className="animate-spin text-plum" />
          </div>
        )}

        {/* Coupon list empty */}
        {!couponsLoading && coupons.length === 0 && !showCouponForm && (
          <div className="bg-mist border border-stone rounded-xl p-6 text-center">
            <Tag size={32} className="mx-auto text-slate mb-3" />
            <p className="text-small text-slate">
              No coupons yet. Click <span className="font-semibold text-midnight">New Coupon</span> to create one.
            </p>
          </div>
        )}

        {/* Coupon list */}
        {!couponsLoading && coupons.length > 0 && (
          <CouponList
            coupons={coupons}
            actionLoading={actionLoading}
            onDeactivate={(coupon) => setDeactivatingCoupon(coupon)}
          />
        )}
      </div>

      {/* ── Platform Stripe Transaction Fee ─────────────────────────────── */}
      {feeLoaded && (
        <section className="bg-white border border-stone rounded-xl p-4 shadow-brand-sm mt-10">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={18} className="text-plum" />
            <h2 className="font-display text-h3 text-midnight">Platform Stripe Transaction Fee</h2>
          </div>
          <p className="text-small text-slate mb-3 leading-relaxed">
            Configure the platform fee applied on Stripe Connect payments.
          </p>

          {/* Fee type selector */}
          <div className="mb-3">
            <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
              Fee Type
            </label>
            <div className="flex gap-2">
              {([
                ['percentage', 'Percentage'],
                ['flat', 'Flat Amount'],
                ['percentage_plus_flat', 'Percentage + Flat'],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFeeType(value)}
                  className={`flex-1 py-2.5 rounded-lg text-small font-medium border transition-colors
                              ${feeType === value
                                ? 'bg-plum text-white border-plum'
                                : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Percentage input */}
          {(feeType === 'percentage' || feeType === 'percentage_plus_flat') && (
            <div className="mb-3">
              <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
                Percentage
              </label>
              <div className="relative w-32">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={feePercentage}
                  onChange={(e) => { setFeePercentage(e.target.value); setFeeSuccess(false); }}
                  className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white text-small text-midnight
                             focus:outline-none focus:ring-2 focus:ring-plum/40 focus:border-plum transition-colors"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate text-small">%</span>
              </div>
            </div>
          )}

          {/* Flat amount input */}
          {(feeType === 'flat' || feeType === 'percentage_plus_flat') && (
            <div className="mb-3">
              <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
                Flat Amount (cents)
              </label>
              <div className="relative w-32">
                <input
                  type="number"
                  min="0"
                  value={feeFlatCents}
                  onChange={(e) => { setFeeFlatCents(e.target.value); setFeeSuccess(false); }}
                  className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white text-small text-midnight
                             focus:outline-none focus:ring-2 focus:ring-plum/40 focus:border-plum transition-colors"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate text-small">¢</span>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleSavePlatformFee}
            disabled={feeSaving}
            className="btn btn-primary px-5"
          >
            {feeSaving ? 'Saving…' : feeSuccess ? 'Saved!' : 'Save Fee'}
          </button>
        </section>
      )}

      {/* Deactivate coupon confirmation */}
      <ConfirmModal
        isOpen={!!deactivatingCoupon}
        title="Deactivate Coupon"
        message={`Are you sure you want to deactivate "${deactivatingCoupon?.name ?? ''}"? Existing discounts on active subscriptions will remain, but no new redemptions will be possible.`}
        confirmLabel="Deactivate"
        variant="danger"
        onConfirm={() => { if (deactivatingCoupon) handleDeactivateCoupon(deactivatingCoupon); }}
        onClose={() => setDeactivatingCoupon(null)}
      />
    </div>
  );
}

// ── Plan Feature Mapping ─────────────────────────────────────────────────

interface PlanFeatureMapping {
  productId: string;
  productName: string;
  allowedFeatures: Partial<Record<PlanFeatureKey, boolean>>;
  updatedAt: string;
  updatedBy: string;
}

function IncludedFeatures({ productId, productName }: { productId: string; productName: string }) {
  const [mapping, setMapping] = useState<PlanFeatureMapping | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState<Partial<Record<PlanFeatureKey, boolean>>>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load existing mapping
  useEffect(() => {
    let cancelled = false;
    api.get<{ success: boolean; data: PlanFeatureMapping }>(`/admin/plan-features/${productId}`)
      .then((res) => {
        if (!cancelled) {
          setMapping(res.data);
          setDraft(res.data.allowedFeatures);
        }
      })
      .catch(() => {
        // 404 = no mapping yet, that's fine
        if (!cancelled) {
          setMapping(null);
          setDraft({});
        }
      })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [productId]);

  const hasMapping = mapping !== null;
  const enabledCount = Object.values(draft).filter(Boolean).length;
  const isDirty = loaded && JSON.stringify(draft) !== JSON.stringify(mapping?.allowedFeatures ?? {});

  const handleToggle = (key: PlanFeatureKey) => {
    setDraft(prev => ({ ...prev, [key]: !prev[key] }));
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await api.put<{ success: boolean; data: PlanFeatureMapping }>(
        `/admin/plan-features/${productId}`,
        { productName, allowedFeatures: draft },
      );
      setMapping(res.data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      setSaveError('Failed to save feature mapping.');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div className="border-t border-stone">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 text-left hover:bg-mist/50 transition-colors flex items-center gap-2"
      >
        {expanded ? <ChevronUp size={14} className="text-slate" /> : <ChevronDown size={14} className="text-slate" />}
        <span className="text-small font-medium text-midnight">Included Features</span>
        {hasMapping ? (
          <span className="text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
            {enabledCount} feature{enabledCount !== 1 ? 's' : ''}
          </span>
        ) : (
          <span className="text-[11px] text-slate/60 italic">Not configured</span>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {!hasMapping && (
            <p className="text-[12px] text-slate mb-3 leading-relaxed">
              No features configured for this plan. Orgs on this plan will have their features managed manually until you configure this.
            </p>
          )}

          <div className="space-y-1">
            {PLAN_FEATURE_OPTIONS.map(({ key, label, desc }) => (
              <label
                key={key}
                className="flex items-start gap-3 py-2 px-2 rounded-lg hover:bg-mist/50 transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!!draft[key]}
                  onChange={() => handleToggle(key)}
                  className="mt-0.5 h-4 w-4 rounded border-stone text-plum focus:ring-plum/30 shrink-0"
                />
                <div className="min-w-0">
                  <span className="text-[13px] font-medium text-midnight">{label}</span>
                  <p className="text-[11px] text-slate leading-snug">{desc}</p>
                </div>
              </label>
            ))}
          </div>

          {saveError && (
            <div className="mt-3 flex items-center gap-2 text-[12px] text-red-600">
              <AlertCircle size={14} />
              {saveError}
            </div>
          )}

          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || (!isDirty && !saveSuccess)}
              className="px-4 py-2 rounded-lg text-[13px] font-medium text-white bg-plum
                         hover:bg-plum-dark disabled:opacity-50 transition-colors
                         flex items-center gap-2"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {saveSuccess ? (
                <>
                  <CheckCircle2 size={14} />
                  Saved
                </>
              ) : (
                'Save Features'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Product Card ────────────────────────────────────────────────────────────

interface ProductCardProps {
  product: ProductItem;
  actionLoading: Record<string, boolean>;
  actionSuccess: string | null;
  isAddPriceOpen: boolean;
  priceForm: NewPriceForm;
  formSaving: boolean;
  formError: string | null;
  onToggleVisibility: () => void;
  onDeactivatePrice: (priceId: string) => void;
  onActivatePrice: (priceId: string) => void;
  onOpenAddPrice: () => void;
  onCloseAddPrice: () => void;
  onUpdateForm: React.Dispatch<React.SetStateAction<NewPriceForm>>;
  onAddTier: () => void;
  onRemoveTier: (index: number) => void;
  onUpdateTier: (index: number, field: keyof NewTier, value: string) => void;
  onSubmitPrice: () => void;
}

function ProductCard({
  product,
  actionLoading,
  actionSuccess,
  isAddPriceOpen,
  priceForm,
  formSaving,
  formError,
  onToggleVisibility,
  onDeactivatePrice,
  onActivatePrice,
  onOpenAddPrice,
  onCloseAddPrice,
  onUpdateForm,
  onAddTier,
  onRemoveTier,
  onUpdateTier,
  onSubmitPrice,
}: ProductCardProps) {
  const [expanded, setExpanded] = useState(true);
  const activePrices = product.prices.filter((p) => p.active);
  const archivedPrices = product.prices.filter((p) => !p.active);
  const [showArchived, setShowArchived] = useState(false);

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm mb-4 overflow-hidden">
      {/* Product header */}
      <div className="p-4 flex items-start gap-3">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg
                     hover:bg-mist transition-colors mt-0.5"
        >
          {expanded ? <ChevronUp size={16} className="text-slate" /> : <ChevronDown size={16} className="text-slate" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-h3 text-midnight">{product.name}</h2>
            {product.visible ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider
                               text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                <Eye size={10} /> Visible
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider
                               text-slate bg-mist border border-stone rounded-full px-2 py-0.5">
                <EyeOff size={10} /> Hidden
              </span>
            )}
          </div>
          {product.description && (
            <p className="text-[13px] text-slate mt-0.5 leading-relaxed">{product.description}</p>
          )}
          <p className="text-[11px] font-mono text-slate/60 mt-1">{product.id}</p>
        </div>

        {/* Visibility toggle */}
        <button
          type="button"
          onClick={onToggleVisibility}
          disabled={!!actionLoading[`vis-${product.id}`]}
          className={`shrink-0 px-3 py-1.5 rounded-lg text-[13px] font-medium
                      border transition-colors ${
                        product.visible
                          ? 'border-stone text-slate hover:border-red-300 hover:text-red-600'
                          : 'border-green-300 text-green-700 hover:bg-green-50'
                      } disabled:opacity-50`}
        >
          {actionLoading[`vis-${product.id}`] ? (
            <Loader2 size={14} className="animate-spin" />
          ) : actionSuccess === `vis-${product.id}` ? (
            <CheckCircle2 size={14} className="text-green-600" />
          ) : product.visible ? (
            'Hide'
          ) : (
            'Make Visible'
          )}
        </button>
      </div>

      {/* Prices */}
      {expanded && (
        <div className="border-t border-stone">
          {/* Active prices */}
          {activePrices.length === 0 && (
            <div className="px-4 py-3">
              <p className="text-[13px] text-slate italic">No active prices. Add one below.</p>
            </div>
          )}

          {activePrices.map((price) => (
            <PriceRow
              key={price.id}
              price={price}
              loading={!!actionLoading[price.id]}
              success={actionSuccess === price.id}
              onArchive={() => onDeactivatePrice(price.id)}
            />
          ))}

          {/* Archived prices toggle */}
          {archivedPrices.length > 0 && (
            <div className="border-t border-stone/50">
              <button
                type="button"
                onClick={() => setShowArchived(!showArchived)}
                className="w-full px-4 py-2 text-left text-[12px] text-slate hover:text-midnight
                           hover:bg-mist/50 transition-colors flex items-center gap-1"
              >
                <Archive size={12} />
                {showArchived ? 'Hide' : 'Show'} {archivedPrices.length} archived price{archivedPrices.length !== 1 ? 's' : ''}
              </button>

              {showArchived && archivedPrices.map((price) => (
                <PriceRow
                  key={price.id}
                  price={price}
                  loading={!!actionLoading[price.id]}
                  success={actionSuccess === price.id}
                  onReactivate={() => onActivatePrice(price.id)}
                  archived
                />
              ))}
            </div>
          )}

          {/* Add Price button / form */}
          <div className="border-t border-stone">
            {!isAddPriceOpen ? (
              <button
                type="button"
                onClick={onOpenAddPrice}
                className="w-full px-4 py-3 text-left text-small font-medium text-plum
                           hover:bg-plum/5 transition-colors flex items-center gap-2"
              >
                <Plus size={16} />
                Add Price
              </button>
            ) : (
              <AddPriceForm
                form={priceForm}
                saving={formSaving}
                error={formError}
                onUpdate={onUpdateForm}
                onAddTier={onAddTier}
                onRemoveTier={onRemoveTier}
                onUpdateTier={onUpdateTier}
                onSubmit={onSubmitPrice}
                onCancel={onCloseAddPrice}
              />
            )}
          </div>

          {/* Included Features */}
          <IncludedFeatures productId={product.id} productName={product.name} />
        </div>
      )}
    </div>
  );
}

// ── Price Row ───────────────────────────────────────────────────────────────

interface PriceRowProps {
  price: PriceItem;
  loading: boolean;
  success: boolean;
  onArchive?: () => void;
  onReactivate?: () => void;
  archived?: boolean;
}

function PriceRow({ price, loading, success, onArchive, onReactivate, archived }: PriceRowProps) {
  return (
    <div className={`px-4 py-3 flex items-start gap-3 border-t border-stone/50 first:border-t-0
                     ${archived ? 'bg-mist/30 opacity-70' : ''}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-small font-semibold text-midnight">
            {price.nickname || intervalLabel(price.interval)}
          </p>
          <span className={`text-[11px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border
                           ${price.interval === 'year'
                             ? 'text-blue-700 bg-blue-50 border-blue-200'
                             : 'text-plum bg-plum/5 border-plum/20'}`}>
            {intervalLabel(price.interval)}
          </span>
          {archived && (
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate
                             bg-stone/50 border border-stone rounded-full px-2 py-0.5">
              Archived
            </span>
          )}
        </div>

        {/* Pricing display */}
        {price.billingScheme === 'tiered' && price.tiers ? (
          <div className="mt-1.5">
            <p className="text-[12px] text-slate uppercase tracking-wider font-semibold mb-1">
              {price.tiersMode === 'volume' ? 'Volume' : 'Graduated'} tiers
            </p>
            {price.tiers.map((tier, i) => (
              <div key={i} className="text-[13px] text-midnight flex items-baseline gap-1">
                <span className="font-mono text-plum font-medium">{formatCents(tier.unitAmount)}</span>
                <span className="text-slate">/user</span>
                <span className="text-slate ml-1">
                  {tier.upTo === null
                    ? `(above ${i > 0 ? price.tiers![i - 1].upTo : 0} users)`
                    : i === 0
                      ? `(first ${tier.upTo} users)`
                      : `(${(price.tiers![i - 1]?.upTo ?? 0) + 1}–${tier.upTo} users)`}
                </span>
              </div>
            ))}
          </div>
        ) : price.nickname?.toLowerCase().includes('flat') ? (
          <p className="text-[13px] text-midnight mt-0.5">
            <span className="font-mono text-plum font-medium">
              {price.unitAmount !== null ? formatCents(price.unitAmount) : '—'}
            </span>
            <span className="text-slate"> /{price.interval === 'year' ? 'year' : 'month'}</span>
            {price.unitAmount === 0 && (
              <span className="ml-1.5 text-[11px] font-semibold uppercase text-green-700 bg-green-50 border border-green-200 rounded-full px-1.5 py-0.5">
                Free
              </span>
            )}
          </p>
        ) : (
          <p className="text-[13px] text-midnight mt-0.5">
            <span className="font-mono text-plum font-medium">
              {price.unitAmount !== null ? formatCents(price.unitAmount) : '—'}
            </span>
            <span className="text-slate"> /user/{price.interval === 'year' ? 'year' : 'month'}</span>
          </p>
        )}

        <p className="text-[11px] font-mono text-slate/60 mt-1">{price.id}</p>
      </div>

      {/* Action button */}
      <div className="shrink-0">
        {loading ? (
          <Loader2 size={16} className="animate-spin text-slate mt-1" />
        ) : success ? (
          <CheckCircle2 size={16} className="text-green-600 mt-1" />
        ) : archived && onReactivate ? (
          <button
            type="button"
            onClick={onReactivate}
            className="flex items-center gap-1 text-[12px] text-plum hover:text-rose
                       font-medium transition-colors py-1"
            title="Re-activate this price"
          >
            <RotateCcw size={14} />
          </button>
        ) : onArchive ? (
          <button
            type="button"
            onClick={onArchive}
            className="flex items-center gap-1 text-[12px] text-slate hover:text-red-600
                       font-medium transition-colors py-1"
            title="Archive this price"
          >
            <Archive size={14} />
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ── Add Price Form ──────────────────────────────────────────────────────────

interface AddPriceFormProps {
  form: NewPriceForm;
  saving: boolean;
  error: string | null;
  onUpdate: React.Dispatch<React.SetStateAction<NewPriceForm>>;
  onAddTier: () => void;
  onRemoveTier: (index: number) => void;
  onUpdateTier: (index: number, field: keyof NewTier, value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

function AddPriceForm({ form, saving, error, onUpdate, onSubmit, onCancel }: AddPriceFormProps) {
  const inputClass = `w-full px-3 py-2.5 rounded-lg border border-stone bg-white text-small text-midnight
                      placeholder:text-slate focus:outline-none focus:ring-2 focus:ring-plum/40
                      focus:border-plum transition-colors`;

  return (
    <div className="p-4 bg-mist/30">
      <h3 className="font-display text-h3 text-midnight mb-3">Add New Price</h3>

      {/* Billing Period */}
      <div className="mb-3">
        <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
          Billing Period
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onUpdate((prev) => ({ ...prev, interval: 'month', nickname: 'Monthly flat rate' }))}
            className={`flex-1 py-2.5 rounded-lg text-small font-medium border transition-colors
                        ${form.interval === 'month'
                          ? 'bg-plum text-white border-plum'
                          : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => onUpdate((prev) => ({ ...prev, interval: 'year', nickname: 'Annual flat rate' }))}
            className={`flex-1 py-2.5 rounded-lg text-small font-medium border transition-colors
                        ${form.interval === 'year'
                          ? 'bg-plum text-white border-plum'
                          : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
          >
            Annual
          </button>
        </div>
      </div>

      {/* Flat Rate amount */}
      <div className="mb-3">
        <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
          Rate (USD)
        </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate text-small">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.unitAmount}
              onChange={(e) => onUpdate((prev) => ({ ...prev, unitAmount: e.target.value }))}
              placeholder="49.00"
              className={`${inputClass} pl-7`}
            />
          </div>
        </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 text-small text-red-600 mb-3">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-lg text-small font-medium border border-stone
                     text-midnight hover:bg-mist transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={saving}
          className="flex-1 btn btn-primary"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Creating…
            </span>
          ) : (
            'Create Price'
          )}
        </button>
      </div>
    </div>
  );
}

// ── Coupon List ──────────────────────────────────────────────────────────

interface CouponListProps {
  coupons: CouponItem[];
  actionLoading: Record<string, boolean>;
  onDeactivate: (coupon: CouponItem) => void;
}

function CouponList({ coupons, actionLoading, onDeactivate }: CouponListProps) {
  const activeCoupons = coupons.filter((c) => c.valid);
  const inactiveCoupons = coupons.filter((c) => !c.valid);

  return (
    <div className="space-y-3">
      {/* Active coupons */}
      {activeCoupons.map((coupon) => (
        <CouponCard
          key={coupon.id}
          coupon={coupon}
          loading={!!actionLoading[`coupon-${coupon.id}`]}
          onDeactivate={() => onDeactivate(coupon)}
        />
      ))}

      {/* Inactive coupons — grayed out at bottom */}
      {inactiveCoupons.length > 0 && (
        <>
          {activeCoupons.length > 0 && (
            <div className="flex items-center gap-2 pt-4 pb-1">
              <div className="flex-1 h-px bg-stone" />
              <span className="text-[12px] text-slate uppercase tracking-wider font-semibold">Inactive</span>
              <div className="flex-1 h-px bg-stone" />
            </div>
          )}
          {inactiveCoupons.map((coupon) => (
            <CouponCard
              key={coupon.id}
              coupon={coupon}
              loading={false}
              inactive
            />
          ))}
        </>
      )}
    </div>
  );
}

// ── Coupon Card ──────────────────────────────────────────────────────────

interface CouponCardProps {
  coupon: CouponItem;
  loading: boolean;
  inactive?: boolean;
  onDeactivate?: () => void;
}

function CouponCard({ coupon, loading, inactive, onDeactivate }: CouponCardProps) {
  const discountLabel = coupon.percentOff
    ? `${coupon.percentOff}% off`
    : coupon.amountOff
      ? `$${(coupon.amountOff / 100).toFixed(2)} off`
      : 'Discount';

  const promoCode = coupon.promotionCodes[0];
  const expiresAt = coupon.redeemBy
    ? new Date(coupon.redeemBy * 1000)
    : promoCode?.expiresAt
      ? new Date(promoCode.expiresAt * 1000)
      : null;
  const isExpired = expiresAt ? expiresAt < new Date() : false;

  return (
    <div className={`bg-white border border-stone rounded-xl shadow-brand-sm p-4
                     ${inactive ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                        ${inactive ? 'bg-stone/50' : 'bg-plum/10'}`}>
          {coupon.percentOff ? (
            <Percent size={18} className={inactive ? 'text-slate' : 'text-plum'} />
          ) : (
            <DollarSign size={18} className={inactive ? 'text-slate' : 'text-plum'} />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-h3 text-midnight">{coupon.name || 'Untitled Coupon'}</h3>
            <span className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-wider
                             rounded-full px-2 py-0.5 border
                             ${inactive || isExpired
                               ? 'text-slate bg-mist border-stone'
                               : 'text-green-700 bg-green-50 border-green-200'}`}>
              {inactive ? 'Inactive' : isExpired ? 'Expired' : 'Active'}
            </span>
          </div>

          {/* Discount amount */}
          <p className="text-small font-semibold text-plum mt-1">{discountLabel}</p>

          {/* Promo code */}
          {promoCode && (
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <span className="text-[12px] text-slate uppercase tracking-wider font-semibold">Code:</span>
              <code className="font-mono text-[13px] text-midnight bg-mist border border-stone rounded px-2 py-0.5">
                {promoCode.code}
              </code>
            </div>
          )}

          {/* Details chips */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className="text-[11px] text-slate bg-mist border border-stone rounded-full px-2 py-0.5">
              {coupon.duration === 'once' ? 'Once' : coupon.duration === 'repeating' ? `${coupon.durationInMonths} months` : 'Forever'}
            </span>
            {promoCode?.restrictions?.firstTimeTransaction && (
              <span className="text-[11px] text-slate bg-mist border border-stone rounded-full px-2 py-0.5">
                First order only
              </span>
            )}
            {promoCode?.restrictions?.customerIds?.length > 0 && (
              <span className="text-[11px] text-slate bg-mist border border-stone rounded-full px-2 py-0.5">
                Specific customer
              </span>
            )}
            {(coupon.maxRedemptions || promoCode?.maxRedemptions) && (
              <span className="text-[11px] text-slate bg-mist border border-stone rounded-full px-2 py-0.5">
                {promoCode?.timesRedeemed ?? coupon.timesRedeemed}/{promoCode?.maxRedemptions ?? coupon.maxRedemptions} redeemed
              </span>
            )}
            {expiresAt && (
              <span className={`text-[11px] border rounded-full px-2 py-0.5
                               ${isExpired
                                 ? 'text-red-600 bg-red-50 border-red-200'
                                 : 'text-slate bg-mist border-stone'}`}>
                {isExpired ? 'Expired' : 'Expires'} {expiresAt.toLocaleDateString()}
              </span>
            )}
          </div>

          <p className="text-[11px] font-mono text-slate/60 mt-2">{coupon.id}</p>
        </div>

        {/* Deactivate button */}
        {!inactive && onDeactivate && (
          <div className="shrink-0">
            {loading ? (
              <Loader2 size={16} className="animate-spin text-slate mt-1" />
            ) : (
              <button
                type="button"
                onClick={onDeactivate}
                className="flex items-center gap-1 text-[12px] text-slate hover:text-red-600
                           font-medium transition-colors py-1"
                title="Deactivate this coupon"
              >
                <Ban size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Create Coupon Form ───────────────────────────────────────────────────

interface CreateCouponFormProps {
  form: NewCouponForm;
  saving: boolean;
  error: string | null;
  products: ProductItem[];
  onUpdate: React.Dispatch<React.SetStateAction<NewCouponForm>>;
  onSubmit: () => void;
  onCancel: () => void;
}

function CreateCouponForm({ form, saving, error, products, onUpdate, onSubmit, onCancel }: CreateCouponFormProps) {
  const inputClass = `w-full px-3 py-2.5 rounded-lg border border-stone bg-white text-small text-midnight
                      placeholder:text-slate focus:outline-none focus:ring-2 focus:ring-plum/40
                      focus:border-plum transition-colors`;

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm p-4 mb-4">
      <h3 className="font-display text-h3 text-midnight mb-1">Create New Coupon</h3>
      <p className="text-[12px] text-slate mb-4">
        Coupons are always customer-facing. Customers can redeem them during checkout.
      </p>

      {/* Name */}
      <div className="mb-3">
        <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
          Coupon Name
        </label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => onUpdate((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. Summer 2026 Promo"
          className={inputClass}
        />
      </div>

      {/* Coupon Code */}
      <div className="mb-3">
        <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
          Coupon Code
        </label>
        <input
          type="text"
          value={form.code}
          onChange={(e) => onUpdate((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
          placeholder="e.g. SUMMER25"
          className={`${inputClass} font-mono uppercase`}
        />
        <p className="text-[12px] text-slate mt-1">
          The code customers will enter at checkout. Will be uppercased automatically.
        </p>
      </div>

      {/* Discount Type */}
      <div className="mb-3">
        <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
          Discount Type
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onUpdate((prev) => ({ ...prev, type: 'percent' }))}
            className={`flex-1 py-2.5 rounded-lg text-small font-medium border transition-colors
                        ${form.type === 'percent'
                          ? 'bg-plum text-white border-plum'
                          : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
          >
            % Discount
          </button>
          <button
            type="button"
            onClick={() => onUpdate((prev) => ({ ...prev, type: 'fixed' }))}
            className={`flex-1 py-2.5 rounded-lg text-small font-medium border transition-colors
                        ${form.type === 'fixed'
                          ? 'bg-plum text-white border-plum'
                          : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
          >
            Fixed Amount Off
          </button>
        </div>
      </div>

      {/* Percent or Amount */}
      <div className="mb-3">
        <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
          {form.type === 'percent' ? 'Percentage Off' : 'Amount Off (USD)'}
        </label>
        <div className="relative">
          {form.type === 'fixed' && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate text-small">$</span>
          )}
          <input
            type="number"
            step={form.type === 'percent' ? '1' : '0.01'}
            min="0"
            max={form.type === 'percent' ? '100' : undefined}
            value={form.type === 'percent' ? form.percentOff : form.amountOff}
            onChange={(e) =>
              onUpdate((prev) => ({
                ...prev,
                ...(form.type === 'percent'
                  ? { percentOff: e.target.value }
                  : { amountOff: e.target.value }),
              }))
            }
            placeholder={form.type === 'percent' ? '25' : '10.00'}
            className={`${inputClass} ${form.type === 'fixed' ? 'pl-7' : ''}`}
          />
          {form.type === 'percent' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate text-small">%</span>
          )}
        </div>
      </div>

      {/* Duration */}
      <div className="mb-3">
        <label className="block text-label uppercase tracking-wider text-slate mb-1 font-bold">
          Duration
        </label>
        <p className="text-[12px] text-slate mb-2">
          How long the discount applies once a customer redeems the coupon.
        </p>
        <div className="flex gap-2">
          {([['once', 'Once'], ['repeating', 'Repeating'], ['forever', 'Forever']] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => onUpdate((prev) => ({ ...prev, duration: value }))}
              className={`flex-1 py-2.5 rounded-lg text-small font-medium border transition-colors
                          ${form.duration === value
                            ? 'bg-plum text-white border-plum'
                            : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-[12px] text-slate mt-2">
          {form.duration === 'once'
            ? 'The discount will apply only to the first invoice after redemption.'
            : form.duration === 'repeating'
              ? 'The discount will apply to every invoice for a set number of months.'
              : 'The discount will apply to every invoice for as long as the subscription is active.'}
        </p>
        {form.duration === 'repeating' && (
          <div className="mt-2">
            <label className="block text-[12px] text-slate mb-1">Number of months</label>
            <input
              type="number"
              min="1"
              value={form.durationInMonths}
              onChange={(e) => onUpdate((prev) => ({ ...prev, durationInMonths: e.target.value }))}
              placeholder="3"
              className={`${inputClass} w-24`}
            />
          </div>
        )}
      </div>

      {/* Applies to specific products */}
      <div className="mb-3">
        <ToggleOption
          label="Applies to specific products only"
          checked={form.appliesToProducts}
          onChange={(v) => onUpdate((prev) => ({ ...prev, appliesToProducts: v, productIds: v ? prev.productIds : [] }))}
        />
        {form.appliesToProducts && products.length > 0 && (
          <div className="mt-2 ml-7 space-y-1.5">
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.productIds.includes(p.id)}
                  onChange={(e) => {
                    onUpdate((prev) => ({
                      ...prev,
                      productIds: e.target.checked
                        ? [...prev.productIds, p.id]
                        : prev.productIds.filter((id) => id !== p.id),
                    }));
                  }}
                  className="w-4 h-4 rounded border-stone text-plum focus:ring-plum/40"
                />
                <span className="text-small text-midnight">{p.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-stone my-4" />

      {/* Options */}
      <p className="text-label uppercase tracking-wider text-slate mb-3 font-bold">Options</p>

      {/* First time order only */}
      <div className="mb-3">
        <ToggleOption
          label="Eligible for first-time order only"
          checked={form.firstTimeOnly}
          onChange={(v) => onUpdate((prev) => ({ ...prev, firstTimeOnly: v }))}
        />
      </div>

      {/* Limit redemptions */}
      <div className="mb-3">
        <ToggleOption
          label="Limit number of times it can be redeemed"
          checked={form.limitRedemptions}
          onChange={(v) => onUpdate((prev) => ({ ...prev, limitRedemptions: v, maxRedemptions: v ? prev.maxRedemptions || '1' : '' }))}
        />
        {form.limitRedemptions && (
          <div className="mt-2 ml-7">
            <input
              type="number"
              min="1"
              value={form.maxRedemptions}
              onChange={(e) => onUpdate((prev) => ({ ...prev, maxRedemptions: e.target.value }))}
              placeholder="1"
              className={`${inputClass} w-24`}
            />
            <p className="text-[12px] text-slate mt-1">Maximum number of redemptions allowed.</p>
          </div>
        )}
      </div>

      {/* Set expiration */}
      <div className="mb-3">
        <ToggleOption
          label="Set an expiration date"
          checked={form.setExpiration}
          onChange={(v) => onUpdate((prev) => ({ ...prev, setExpiration: v, expiresAt: v ? prev.expiresAt : '' }))}
        />
        {form.setExpiration && (
          <div className="mt-2 ml-7">
            <input
              type="date"
              value={form.expiresAt}
              onChange={(e) => onUpdate((prev) => ({ ...prev, expiresAt: e.target.value }))}
              min={new Date().toISOString().split('T')[0]}
              className={`${inputClass} w-48`}
            />
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 text-small text-red-600 mb-3 mt-4">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-lg text-small font-medium border border-stone
                     text-midnight hover:bg-mist transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={saving}
          className="flex-1 btn btn-primary"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Creating…
            </span>
          ) : (
            'Create Coupon'
          )}
        </button>
      </div>
    </div>
  );
}

// ── Toggle Option ────────────────────────────────────────────────────────

interface ToggleOptionProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function ToggleOption({ label, checked, onChange }: ToggleOptionProps) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex items-center gap-2.5 w-full text-left py-1"
    >
      <div className={`shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                       ${checked
                         ? 'bg-plum border-plum'
                         : 'bg-white border-stone hover:border-plum/40'}`}>
        {checked && <CheckCircle2 size={14} className="text-white" />}
      </div>
      <span className="text-small text-midnight">{label}</span>
    </button>
  );
}
